import React, { useEffect, useRef, useState } from 'react';
import ReactTable, { Column, ComponentPropsGetterR, Filter, SortingRule, SubComponentFunction, ExpandedChangeFunction } from "react-table";
import { DataTablePagination } from './DataTablePagination';
import { useFilterableRows } from './useFilterableRows';
import './DataTable.scss';
import 'react-table/react-table.css'
import { filterCaseInsensitive } from './filterCaseInsensitive';
import { filterCaseInsensitiveNoSymbol } from './filterCaseInsensitiveNoSymbol';
import getAccessors from './getAccessors';
import { useSelections } from './useSelections';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import selectTableHOC from 'react-table/lib/hoc/selectTable';
import useDebounce from '../../utils/useDebounce';
import { RenderProp, handleRenderProp } from '../../utils/handleRenderProp';

export interface DataTableFilter extends Filter { }

export interface DataTableSorting extends SortingRule { }

export interface DataTableColumn<T> extends Column<T> { key?: string; }

export interface DataTableState {
    page: number;
    pageSize: number;
    filtered: DataTableFilter[];
    sorted: DataTableSorting[];
}

export interface DataTableProps<T = any> {
    data: T[];
    /** Column definition array */
    columns: DataTableColumn<T>[];
    striped?: boolean;
    showPaginationTop?: boolean;
    showPaginationBottom?: boolean;
    minRows?: number;
    defaultPageSize?: number;
    pageSizeOptions?: number[];
    onRowClick?: (row: T) => void;
    /** provides you the row so you can return true to indicate whether its selected, or false, based on its properties */
    isRowSelected?: (row: T) => boolean;
    getRowProps?: ComponentPropsGetterR;
    /** server-side paging function that triggers whenever sorting, filtering, etc is required.  props.pages is required when this is used. */
    onFetchData?: (state: DataTableState, globalFilter: string) => Promise<any>;
    /** debounce value, defaults to 250ms */
    fetchDelay?: number;
    /** total number of pages, required when doing server-side paging with onFetchData */
    pages?: number;
    totalRows?: number;

    sortable?: boolean;
    defaultSorted?: DataTableSorting[];
    /** enables per-column filtering */
    filterable?: boolean;

    SubComponent?: SubComponentFunction;

    expanded?: {};

    onExpandedChange?: ExpandedChangeFunction;

    //our props, not from ReactTable

    /** names of keys to filter on for global filter */
    filterOn?: string[];
    noDataMessage?: string;
    ignoreSymbolsWhenFiltering?: boolean;

    //selectTable-specific props

    /** name of the Primary key column in the row.  ID, CaseID, etc */
    keyField?: string;
    /** list of selected IDs. */
    selectedKeys?: any[];
    /** alternative to keyField, a function that returns the key to use. */
    getKeyFn?: (row: T) => any;
    /** optional callback to provide you with the selected IDs array */
    onChangeSelections?: (newSelections: any[]) => void;
    /** HTML id */
    id?: string;
    /** show loading indicators */
    loading?: boolean;
    style?: object | undefined;

    /** Other actions rendered in pager next to search */
    gridActions?: JSX.Element | RenderProp | string;
}

export interface SelectAllInputComponentProps {
    totalAmount: number;
    filter: string;
    selectType: 'checkbox' | 'radio';
    checked: boolean;
    onClick: () => any;
    id: string;
}
export const SelectAllInputComponent = (props: SelectAllInputComponentProps) => {
    let tooltipText = '';
    if (props.filter) {
        tooltipText = `Select ${props.totalAmount} entries matching: ${props.filter}`;
    } else {
        tooltipText = `Select all ${props.totalAmount} entries`;
    }
    return (
        <OverlayTrigger
            overlay={<Tooltip id={props.id + '-select-all-tooltip'}>{tooltipText}</Tooltip>}
            placement="right"
            delayShow={150}
            delayHide={100}
        >
            <input
                type={props.selectType || 'checkbox'}
                checked={props.checked}
                onClick={e => {
                    e.stopPropagation();
                    props.onClick();
                }}
                onChange={() => { }}
            />
        </OverlayTrigger>
    )
}

const ReactTableWithSelect = selectTableHOC(ReactTable);

export const DataTable: React.FC<DataTableProps> = (props) => {
    const { data, onRowClick, children, onFetchData, pageSizeOptions, ...otherProps } = props;
    const useDataFetching:boolean = !!props.onFetchData;
    const makeSelectable = props.onChangeSelections && (props.keyField || props.getKeyFn);

    const NoDataComponent = () => <div className="no-rows-found">{props.noDataMessage || 'No rows found'}</div>;
    let _defaultGetKeyFn = (row:any) => row[props.keyField || ''];
    let _getKeyFn = props.getKeyFn || _defaultGetKeyFn;

    const defaultPageSize = props.defaultPageSize || 10;

    const _ignoreSymbolsWhenFiltering = props.ignoreSymbolsWhenFiltering === undefined ? true : !!(props.ignoreSymbolsWhenFiltering)

    const tableRef = useRef<ReactTable<any>>(null);
    const { currentRows, setFilter, filter } = useFilterableRows(data, getAccessors(props.columns), props.filterOn, _ignoreSymbolsWhenFiltering);
    const { selectedKeys, ...selectionProps } = useSelections({ rows: currentRows, getKey: _getKeyFn, selectedKeys: props.selectedKeys });

    const debouncedFilter = useDebounce(filter, props.fetchDelay || 500);
    const [debouncing, setDebouncing] = useState(false);
    useEffect(() => {
        if (useDataFetching && tableRef.current != null) {
            (tableRef.current as any).fireFetchData();
        }
        setDebouncing(false);
    }, [debouncedFilter, tableRef, useDataFetching]);

    //hook callbacks from props into useSelections
    const selectTableProps = {
        selectAll: selectionProps.selectAll,
        selectType: selectionProps.selectType,
        toggleAll: function () {
            if (!props.onChangeSelections) return;
            const newSelections = selectionProps.toggleAll();
            props.onChangeSelections(newSelections);
        },
        toggleSelection: function (key: any, shiftKeyPressed: boolean, row: any) {
            if (!props.onChangeSelections) return;
            const rowKey = props.getKeyFn ? props.getKeyFn(row) : row.id;
            const newSelections = selectionProps.toggleSelection(rowKey, shiftKeyPressed, row);
            props.onChangeSelections(newSelections);
        }
    }
    const isSelectedFn = (key: string) => !!props.selectedKeys && props.selectedKeys.includes(key);

    let css = "";
    if (props.striped) css = "-striped";

    const useTopPagination = props.showPaginationTop != null ? props.showPaginationTop : true;
    const useBottomPagination = props.showPaginationBottom != null ? props.showPaginationBottom : true;
    const useMinRows = props.minRows != null ? props.minRows : 1;
    const makeSortable = props.sortable != null ? props.sortable : true;
    const makeFilterable = props.filterable != null ? props.filterable : false;

    const _onFetchData = (state: DataTableState, instance: any) => {
        if (!!onFetchData) {
            setTimeout(() => onFetchData(state, filter), 0);
        }
    }

    const getTrProps: ComponentPropsGetterR = (state, rowInfo: any, column, instance) => {
        let trProps: any = {
            className: ''
        };
        if (makeSelectable) {
            trProps.style = {
                cursor: 'pointer',
                'user-select': 'none'
            };
        }
        
        if (onRowClick || makeSelectable) {
            trProps.onClick = function(e: any, handleOriginal: any) {
                const isRowClick = e.target 
                    && e.target.className 
                    && /rt-/.test(e.target.className);

                if (makeSelectable && rowInfo) {
                    selectTableProps.toggleSelection(rowInfo.original.id, false, rowInfo.original);   
                }

                if (onRowClick && rowInfo && rowInfo.original && isRowClick) {
                    onRowClick(rowInfo.original);
                }

                if (handleOriginal) handleOriginal();
            },
            trProps.className += "clickable";
        }

        if (props.getRowProps) {
            let incomingRowProps = props.getRowProps(state, rowInfo, column, instance) || {};
            trProps = { ...trProps, ...incomingRowProps };
        }

        const row = rowInfo && rowInfo.original ? rowInfo.original : null;

        if (row && (makeSelectable || props.isRowSelected)) {
            let isSelected = props.isRowSelected ? props.isRowSelected(row) : isSelectedFn(row[props.keyField!]);
            if (isSelected) {
                trProps.className += ' row-selected';
            }
        }

        return trProps;
    }

    let table: JSX.Element;
    let finalTableProps: any = {
        ref: tableRef,
        className: css,
        sortable: makeSortable,
        filterable: makeFilterable,
        minRows: useMinRows,
        getTrProps: getTrProps,
        defaultPageSize: defaultPageSize,
        showPaginationBottom: useBottomPagination,
        showPaginationTop: useTopPagination,
        setFilter: setFilter,
        filter: filter,
        PaginationComponent: DataTablePagination,
        pageSizeOptions: pageSizeOptions,
        manual: useDataFetching ? true : undefined,
        onFetchData: useDataFetching ? _onFetchData : undefined,
        data: currentRows,
        debouncing: debouncing,
        setDebouncing: setDebouncing,
        useDataFetching,
        NoDataComponent: NoDataComponent,
        defaultFilterMethod: _ignoreSymbolsWhenFiltering ? filterCaseInsensitiveNoSymbol : filterCaseInsensitive,
        loading: props.loading || undefined,
        style: props.style,
        gridActions: props.gridActions 
    }

    if (makeSelectable) {
        finalTableProps.isSelected = isSelectedFn;
        finalTableProps.SelectAllInputComponent = (p:any) => <SelectAllInputComponent {...p} filter={filter} id={props.id || 'select-table'} totalAmount={currentRows.length} />
        table = <ReactTableWithSelect
            {...otherProps}
            {...finalTableProps}
            {...selectTableProps} />
    } else {
        table = <ReactTable 
            {...otherProps}
            {...finalTableProps}
        />
    }

    return (
        <div className="ts-datatable">
            {table}
        </div>
    )
}

(DataTable as any).displayName = "DataTable";